import React, { useMemo, useRef, useState } from 'react';

/**
 * Throughput chart — for counter deltas (images processed, patients synced).
 *
 * A running total answers "how many altogether"; this answers "how much work
 * in each period". Two things make that reading honest:
 *
 *  - Bars are drawn from the baseline and fill their whole bucket width, so a
 *    bar visibly *is* the period it covers. Plain markers would suggest an
 *    instantaneous reading, which a delta is not.
 *  - A bucket with no data is drawn as a faint outlined slot, never as an
 *    absent bar. Absence and zero are different facts: "the device reported
 *    nothing" must not look like "the device did nothing".
 *
 * Above a density threshold bars are too thin to read, so the same values are
 * drawn as a step line instead — flat across each bucket, stepping at the
 * boundaries, which preserves the "held for this period" meaning.
 */

interface Props {
  timestamps: string[];
  values: (number | null)[];
  color?: string;
  /** Suffix for the hover readout, e.g. '/15m' or '/min'. */
  unit?: string;
  height?: number;
  hoverIndex?: number | null;
  onHoverIndexChange?: (index: number | null) => void;
  zoom?: [number, number] | null;
  onZoomChange?: (range: [number, number] | null) => void;
  onExpand?: () => void;
  expandable?: boolean;
}

const PAD = { top: 12, right: 12, bottom: 22, left: 48 };

// Below this many buckets bars stay wide enough to read; above it they would be
// slivers, so the step line takes over.
const MAX_BARS = 60;

const fmt = (v: number) =>
  Math.abs(v) >= 10000 ? `${(v / 1000).toFixed(1)}k`
    : Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : Number.isInteger(v) ? String(v)
        : v.toFixed(2);

export const ThroughputChart: React.FC<Props> = ({
  timestamps,
  values,
  color = '#34d399',
  unit = '',
  height = 150,
  hoverIndex,
  onHoverIndexChange,
  zoom = null,
  onZoomChange,
  onExpand,
  expandable = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [localHover, setLocalHover] = useState<number | null>(null);
  const [drag, setDrag] = useState<[number, number] | null>(null);

  const synced = onHoverIndexChange !== undefined;
  const absHover = synced ? (hoverIndex ?? null) : localHover;
  const setHover = (i: number | null) =>
    synced ? onHoverIndexChange?.(i) : setLocalHover(i);

  React.useEffect(() => {
    if (!drag) return;
    const finish = () => setDrag(null);
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [drag]);

  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const [z0, z1] = zoom
    ? [Math.max(0, zoom[0]), Math.min(values.length - 1, zoom[1])]
    : [0, values.length - 1];
  const view = useMemo(() => values.slice(z0, z1 + 1), [values, z0, z1]);
  const viewTimes = useMemo(() => timestamps.slice(z0, z1 + 1), [timestamps, z0, z1]);

  const numeric = view.filter((v): v is number => typeof v === 'number');
  // Throughput starts at zero: a bar's height must be readable as its amount.
  const hi = useMemo(() => {
    if (numeric.length === 0) return 1;
    const max = Math.max(...numeric);
    return max <= 0 ? 1 : max * 1.15;
  }, [numeric]);

  const plotW = Math.max(10, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const asBars = view.length <= MAX_BARS;

  // Bars occupy their bucket's slot; the step line uses slot centres.
  const slotW = plotW / Math.max(1, view.length);
  const slotX = (i: number) => PAD.left + i * slotW;
  const centreX = (i: number) => slotX(i) + slotW / 2;
  const y = (v: number) => PAD.top + plotH - (v / hi) * plotH;
  const baseline = PAD.top + plotH;

  const yTicks = useMemo(() => {
    const n = 3;
    const ticks = Array.from({ length: n + 1 }, (_, k) => (hi * k) / n);
    // Counts are whole numbers; avoid 0.33 / 0.67 on small ranges.
    return hi <= n ? Array.from(new Set(ticks.map(Math.round))) : ticks;
  }, [hi]);

  const xTicks = useMemo(() => {
    if (viewTimes.length === 0) return [];
    const want = Math.max(2, Math.min(7, Math.floor(plotW / 95)));
    const stride = Math.max(1, Math.floor((viewTimes.length - 1) / (want - 1)));
    const idx: number[] = [];
    for (let i = 0; i < viewTimes.length; i += stride) idx.push(i);
    return idx;
  }, [viewTimes, plotW]);

  const multiDay = useMemo(() => {
    if (viewTimes.length < 2) return false;
    const ms = +new Date(viewTimes[viewTimes.length - 1]) - +new Date(viewTimes[0]);
    return ms > 36 * 3600 * 1000;
  }, [viewTimes]);

  const tickLabel = (iso: string) => {
    const d = new Date(iso);
    return multiDay
      ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // Step path: hold each value across its bucket, break on missing data.
  const stepRuns = useMemo(() => {
    const runs: string[] = [];
    let d = '';
    view.forEach((v, i) => {
      if (typeof v !== 'number') {
        if (d) { runs.push(d); d = ''; }
        return;
      }
      const x0 = slotX(i);
      const x1 = slotX(i) + slotW;
      d += d ? ` L ${x0},${y(v)}` : `M ${x0},${y(v)}`;
      d += ` L ${x1},${y(v)}`;
    });
    if (d) runs.push(d);
    return runs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, slotW, hi, plotH, width, height]);

  const indexAt = (clientX: number, el: SVGSVGElement) => {
    const rect = el.getBoundingClientRect();
    const px = clientX - rect.left;
    if (view.length === 0) return null;
    return Math.max(0, Math.min(view.length - 1, Math.floor((px - PAD.left) / slotW)));
  };

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const i = indexAt(e.clientX, e.currentTarget);
    if (i === null) return;
    setHover(z0 + i);
    if (drag) setDrag([drag[0], i]);
  };

  const onUp = () => {
    if (!drag) return;
    const [a, b] = drag;
    const [from, to] = a <= b ? [a, b] : [b, a];
    setDrag(null);
    if (to - from < 2) { onExpand?.(); return; }
    onZoomChange?.([z0 + from, z0 + to]);
  };

  if (view.length === 0) {
    return (
      <div
        ref={wrapRef}
        style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
                 fontSize: 12, color: 'rgba(255,255,255,0.3)' }}
      >
        No readings in this window
      </div>
    );
  }

  const hoverLocal =
    absHover !== null && absHover >= z0 && absHover <= z1 ? absHover - z0 : null;
  const hoverValue = hoverLocal !== null ? view[hoverLocal] : null;
  const barW = Math.max(1, slotW - (slotW > 6 ? 2 : 0.5));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <svg
        width={width}
        height={height}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        onMouseDown={(e) => {
          const i = indexAt(e.clientX, e.currentTarget);
          if (i !== null) setDrag([i, i]);
        }}
        onMouseUp={onUp}
        style={{ display: 'block', cursor: drag ? 'ew-resize' : 'crosshair', userSelect: 'none' }}
      >
        {/* gridlines + labels */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)}
              stroke="rgba(255,255,255,0.06)"
            />
            <text
              x={PAD.left - 6} y={y(t) + 3} textAnchor="end"
              fontSize={10} fill="rgba(255,255,255,0.35)"
            >
              {fmt(t)}
            </text>
          </g>
        ))}

        {/* baseline: throughput is measured from zero */}
        <line
          x1={PAD.left} x2={PAD.left + plotW} y1={baseline} y2={baseline}
          stroke="rgba(255,255,255,0.18)"
        />

        {xTicks.map((i) => (
          <text
            key={i} x={centreX(i)} y={height - 6} textAnchor="middle"
            fontSize={10} fill="rgba(255,255,255,0.35)"
          >
            {tickLabel(viewTimes[i])}
          </text>
        ))}

        {asBars
          ? view.map((v, i) => {
              // No reading: an outlined slot, so absence never reads as zero.
              if (typeof v !== 'number') {
                return (
                  <rect
                    key={i}
                    x={slotX(i) + (slotW - barW) / 2} y={PAD.top}
                    width={barW} height={plotH}
                    fill="rgba(255,255,255,0.03)"
                    stroke="rgba(255,255,255,0.08)"
                    strokeDasharray="2 2"
                  />
                );
              }
              // A real zero: a thin stub on the baseline, visibly "measured 0".
              const h = Math.max(v > 0 ? 1.5 : 1, baseline - y(v));
              return (
                <rect
                  key={i}
                  x={slotX(i) + (slotW - barW) / 2}
                  y={baseline - h}
                  width={barW} height={h}
                  fill={v > 0 ? color : 'rgba(255,255,255,0.22)'}
                  opacity={hoverLocal === i ? 1 : 0.8}
                  rx={slotW > 6 ? 1.5 : 0}
                />
              );
            })
          : stepRuns.map((d, i) => (
              <path
                key={i} d={d} fill="none" stroke={color}
                strokeWidth={1.75} strokeLinejoin="round"
              />
            ))}

        {drag && Math.abs(drag[1] - drag[0]) >= 1 && (
          <rect
            x={slotX(Math.min(drag[0], drag[1]))} y={PAD.top}
            width={Math.abs(drag[1] - drag[0] + 1) * slotW} height={plotH}
            fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.3)"
          />
        )}

        {hoverLocal !== null && !drag && (
          <line
            x1={centreX(hoverLocal)} x2={centreX(hoverLocal)}
            y1={PAD.top} y2={baseline}
            stroke="rgba(255,255,255,0.25)" strokeDasharray="3 3"
          />
        )}
      </svg>

      {hoverLocal !== null && !drag && (
        <div
          style={{
            position: 'absolute', top: 2,
            left: Math.min(Math.max(centreX(hoverLocal) - 62, 0), Math.max(width - 132, 0)),
            pointerEvents: 'none',
            backgroundColor: 'rgba(10,10,10,0.94)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8, padding: '6px 9px', minWidth: 120,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#ffffff' }}>
            {typeof hoverValue === 'number'
              ? `${fmt(hoverValue)}${unit}`
              : 'No reading'}
          </div>
          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>
            {typeof hoverValue === 'number' && hoverValue === 0 && 'nothing processed · '}
            {new Date(viewTimes[hoverLocal]).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </div>
        </div>
      )}

      {zoom && onZoomChange && (
        <button
          onClick={(e) => { e.stopPropagation(); onZoomChange(null); }}
          style={{
            position: 'absolute', top: 2, right: 2, fontSize: 10,
            padding: '3px 7px', borderRadius: 6, cursor: 'pointer',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)',
          }}
        >
          Reset zoom
        </button>
      )}

      {expandable && !zoom && (
        <div
          style={{
            position: 'absolute', top: 2, right: 4, fontSize: 9.5,
            color: 'rgba(255,255,255,0.22)', pointerEvents: 'none',
          }}
        >
          {asBars ? 'click to expand · drag to zoom' : `${view.length} buckets · click to expand`}
        </div>
      )}
    </div>
  );
};
